import TabSingleField from './fields/TabSingleField';
import ObjectHelper from '../../../components/ObjectHelper';

class FieldsList {
	constructor() {
		// We use Set as key to have possibility to use multiple fields with the same name.
		// Like autocomplete for desktop and mobile at the same time (iPad in portrait and album orientation).

		/** @type {Map<string, Set<BaseField>>} */
		this._fields = new Map();
		this._hasTabSingleField = false;
	}

	get size() {
		return this._fields.size;
	}

	getAllFieldsValues(onlyVisible = false) {
		const fieldValues = [];

		for (const fields of this._fields.values()) {
			for (const field of fields) {
				if (!onlyVisible || !field.isHidden()) {
					fieldValues.push(field);
				}
			}
		}

		return fieldValues
	}

	_getFieldsSetByKey(key) {
		const fields = this._fields.get(key);
		const fieldsToReturn = fields ? Array.from(fields) : [];

		return fieldsToReturn;
	}

	/**
	 * Adds filter field.
	 * @param {BaseField} filterField
	 */
	add(filterField) {
		if (!this._fields.has(filterField.name)) {
			this._fields.set(filterField.name, new Set());
		}

		const fieldsSet = this._fields.get(filterField.name);
		fieldsSet.add(filterField);

		if (filterField instanceof TabSingleField) {
			this._hasTabSingleField = true;
		}
	}

	getFirst(fieldName) {
		const fieldsSet = this._fields.get(fieldName);

		if (!fieldsSet) {
			return null;
		}

		let first = null

		if (fieldsSet.size > 0) {
			[first] = fieldsSet;
		}

		return first;
	}

	getFieldsNameAndValue(onlyVisible = false, ignoreTabSingle = false) {
		const filters = {};

		if (!ignoreTabSingle && this._hasTabSingleField) {
			return this._getFieldsNameAndValueForTabSingleField();
		}

		for (const field of this.getAllFieldsValues(onlyVisible)) {
			const {fieldName, fieldValue} = field.getNameAndValue();
			filters[fieldName] = fieldValue;
		}

		return filters;
	}

	_getFieldsNameAndValueForTabSingleField() {
		const filters = {};

		for (const [fieldName, fields] of this._fields.entries()) {
			if (fieldName.endsWith('-tab')) {
				const [firstField] = fields;

				if (firstField) {
					const fieldNameWithoutTab = fieldName.replace('-tab', '');
					filters[fieldNameWithoutTab] = firstField.getValue();
				}
			}
		}

		return filters;
	}

	getFieldsNameAndLabel(onlyVisible = false) {
		const filters = {};

		for (const field of this.getAllFieldsValues(onlyVisible)) {
			let {fieldName, fieldLabel} = field.getNameAndLabel();
			fieldName = field instanceof TabSingleField ? fieldName + '-tab' : fieldName;

			filters[fieldName] = fieldLabel;
		}

		return filters;
	}

	getNameAndDeeplinks() {
		const filters = {};

		for (const field of this.getAllFieldsValues()) {
			const {fieldName, fieldDeeplinks} = field.getNameAndDeeplinks();
			filters[fieldName] = fieldDeeplinks;
		}

		return filters;
	}

	getFilterValue(filterName) {
		let val = null;
		const fields = this._fields.get(filterName);
		// TODO test this properly
		const firstField = fields ? Array.from(fields)[0] : null;

		if (firstField) {
			val = firstField.getValue();
		}

		return val;
	}

	setValueAndTriggerEvent(filterName, filterValue) {
		if (this._hasTabSingleField) {
			filterName += '-tab';
		}

		const fields = this._getFieldsSetByKey(filterName);
		let index = 0;
		const isLast = () => (index + 1) === fields.length;

		for (const field of fields) {
			if (isLast()) {
				field.setValueAndTriggerEvent(filterValue);
			} else {
				field.setValue(filterValue);
			}

			++index;
		}
	}

	setFilters(filters) {
		for (const filterName in filters) {
			const filterValue = filters[filterName];

			for (const field of this._getFieldsSetByKey(filterName)) {
				field.setValue(filterValue);
			}
		}
	}

	getVendorsToShowAndOtherData(exclusions = null) {
		let vendorsToShow = new Set();
		let noFilters = true;
		let hasVendorsWithOrder = false;
		const selectedFilters = {};

		for (const field of this.getAllFieldsValues()) {
			if (!field.getValue() || field.isLinkTheSameAsCurrentUrl) {
				continue;
			}

			selectedFilters[field.name] = field.getValue();
			noFilters = false;
			const vendors = field.getVendors();
			const vendorsWithOrder = field.getVendorsWithOrder();

			if (Array.isArray(vendorsWithOrder) && vendorsWithOrder.length) {
				vendorsToShow = this._buildAndgetOrderedVendorsToShow(vendorsToShow, vendorsWithOrder);
				hasVendorsWithOrder = true;
			} else {
				this._handleVendorsToShowForVendors(vendorsToShow, vendors);
			}
		}

		this._applyExclusionsIfNeeded({vendorsToShow, exclusions, selectedFilters});

		return {vendorsToShow, noFilters, hasVendorsWithOrder};
	}

	/**
	 * @param {Set<string>} existVendorsToShow
	 * @param {string[]} vendorsWithOrder
	 * @private
	 */
	_buildAndgetOrderedVendorsToShow(existVendorsToShow, vendorsWithOrder) {
		return new Set(vendorsWithOrder.filter(vendorSlug => {
			return existVendorsToShow.size === 0 || existVendorsToShow.has(vendorSlug);
		}));
	}

	_handleVendorsToShowForVendors(vendorsToShow, vendors) {
		if (vendors === null) {
			return vendorsToShow;
		}

		if (vendorsToShow.size === 0) {
			for (const vendorSlug in vendors) {
				vendorsToShow.add(vendorSlug);
			}
		} else {
			const vendorsKeys = Object.keys(vendors);

			for (const vendorsToShowElement of vendorsToShow) {
				if (!vendorsKeys.includes(vendorsToShowElement)) {
					vendorsToShow.delete(vendorsToShowElement);
				}
			}
		}
	}

	/**
	 * Applies FiltersExclusions
	 * @param {Set<string>} vendorsToShow
	 * @param {Object} exclusions
	 * @param {Object} selectedFilters
	 * @private
	 */
	_applyExclusionsIfNeeded({vendorsToShow, exclusions, selectedFilters}) {
		if (!vendorsToShow.size || ObjectHelper.isEmpty(exclusions) || ObjectHelper.isEmpty(selectedFilters)) {
			return;
		}

		for (const exclusionItem of Object.values(exclusions)) {
			const exclusionItemFilters = {...exclusionItem};
			delete exclusionItemFilters.vendor_slug;

			let isExcluded = true;

			for (const [excludedFilterName, excludedFilterValue] of Object.entries(exclusionItemFilters)) {
				if (!excludedFilterValue) {
					continue;
				}

				if (selectedFilters[excludedFilterName] !== excludedFilterValue) {
					isExcluded = false;
					break;
				}
			}

			if (isExcluded) {
				vendorsToShow.delete(exclusionItem.vendor_slug);
			}
		}
	}
}

export default FieldsList;
